import logger from '../../utils/logging/logger';

// Adaptive request timeout for HttpClient: detects slow networks (Network
// Information API) and firewall/DPI latency patterns, adjusting client.timeout.
// `client` is the HttpClient instance.
  /**
   * Detect slow network conditions and adjust timeout accordingly
   */
export function updateAdaptiveTimeout(client) {
    // Check Network Information API if available
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    
    if (connection) {
      const effectiveType = connection.effectiveType;
      const downlink = connection.downlink;
      const rtt = connection.rtt;
      
      // Detect slow network conditions
      const isSlowNetwork = effectiveType === 'slow-2g' || 
                           effectiveType === '2g' ||
                           (effectiveType === '3g' && downlink < 1.5) ||
                           rtt > 1000; // RTT > 1 second
      
      client.slowNetworkDetected = isSlowNetwork;
      
      if (isSlowNetwork) {
        // Increase timeout for slow networks (30 seconds)
        client.timeout = 30000;
        logger.debug('Slow network detected, timeout increased to 30s');
      } else {
        // Use base timeout for normal networks
        client.timeout = client.baseTimeout;
      }
    }
    
      // Check if firewall might be present based on latency history
      if (client.requestLatencies.length >= 3) {
        const avgLatency = client.requestLatencies.reduce((a, b) => a + b, 0) / client.requestLatencies.length;
        const maxLatency = Math.max(...client.requestLatencies);
        
        // Firewall likely present if:
        // - Average latency > 2 seconds, OR
        // - Max latency > 5 seconds, OR
        // - High variance in latencies (suggests DPI)
        const variance = client.requestLatencies.reduce((acc, lat) => acc + Math.pow(lat - avgLatency, 2), 0) / client.requestLatencies.length;
        const isHighVariance = variance > 1000000; // Variance > 1 second squared
        
        if (avgLatency > 2000 || maxLatency > 5000 || isHighVariance) {
          if (!client.firewallDetected) {
            client.firewallDetected = true;
            logger.warn('Firewall detected based on latency patterns. Timeout increased.', { avgLatency, maxLatency, variance });
            // Dispatch custom event for UI to show warning
            if (typeof window !== 'undefined') {
              window.dispatchEvent(new CustomEvent('firewall-detected', { 
                detail: { avgLatency, maxLatency, variance } 
              }));
            }
          }
          // Increase timeout for firewall environments (25 seconds)
          client.timeout = Math.max(client.timeout, 25000);
        } else if (client.firewallDetected && avgLatency < 1000 && maxLatency < 2000) {
          // Reset firewall detection if latency improves
          client.firewallDetected = false;
          client.timeout = client.slowNetworkDetected ? 30000 : client.baseTimeout;
          logger.debug('Firewall detection reset - latency improved');
          // Dispatch event to clear warning
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('firewall-cleared'));
          }
        }
      }
    }
  
  /**
   * Record request latency for adaptive timeout
   */
export function recordLatency(client, latency) {
    client.requestLatencies.push(latency);
    if (client.requestLatencies.length > client.maxLatencyHistory) {
      client.requestLatencies.shift(); // Keep only last N latencies
    }
    // Update adaptive timeout periodically
    if (client.requestLatencies.length % 3 === 0) {
      client.updateAdaptiveTimeout();
    }
  }
